import React from "react";
import { IoIosArrowRoundForward } from "react-icons/io";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FaShoppingCart } from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import { ProductSkeleton } from "../../components/common/ProductSkeleton";
import { Autoplay, Navigation } from "swiper/modules";
import { useCart } from "../../hooks/CartContext";
import toast from "react-hot-toast";

const RelatedProduct = ({ category }) => {
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const { data, isPending, isError } = useQuery({
    queryKey: ["related-products", category],
    enabled: Boolean(category),
    queryFn: async () => {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/products/category/${category}`
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || "Failed to fetch related products");
      return json;
    },
  });

  const relatedProducts = data?.products ?? [];

  if (!category) return null;

  return (
    <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-7">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-gray-900 font-bold text-xl sm:text-2xl">
          Related <span className="text-orange-500">Products</span>
        </h2>

        <Link
          to="/consoles"
          className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold border border-gray-200 hover:bg-gray-50 transition"
        >
          See All <IoIosArrowRoundForward size={20} />
        </Link>
      </div>

      {/* Slider */}
      <div className="mt-5">
        <Swiper
          modules={[Navigation, Autoplay]}
          loop={relatedProducts.length > 5}
          navigation
          autoplay={{
            delay: 4000,
            disableOnInteraction: false,
          }}
          breakpoints={{
            0: { slidesPerView: 1.15, spaceBetween: 12 },
            480: { slidesPerView: 2, spaceBetween: 14 },
            640: { slidesPerView: 2.4, spaceBetween: 14 },
            1024: { slidesPerView: 4, spaceBetween: 18 },
            1280: { slidesPerView: 5, spaceBetween: 20 },
          }}
          className="rounded-2xl"
          style={{ "--swiper-navigation-color": "#111827" }}
        >
          {/* Loading */}
          {isPending &&
            Array.from({ length: 8 }).map((_, i) => (
              <SwiperSlide key={i}>
                <ProductSkeleton />
              </SwiperSlide>
            ))}

          {/* Error */}
          {isError && (
            <SwiperSlide>
              <div className="bg-gray-50 rounded-2xl border border-gray-100 p-6">
                <p className="text-red text-sm">Failed to load related products.</p>
              </div>
            </SwiperSlide>
          )}

          {/* Items */}
          {!isPending &&
            !isError &&
            relatedProducts.map((p) => {
              const soldOut = Number(p?.quantity || 0) < 1;

              return (
                <SwiperSlide key={p?._id}>
                  <div
                    onClick={() => navigate(`/product/${p?._id}`)}
                    className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md cursor-pointer transition overflow-hidden"
                  >
                    <img
                      src={p?.images?.[0] || "/placeholder.png"}
                      alt={p?.name || "product"}
                      className="h-40 sm:h-48 w-full object-cover bg-gray-100"
                    />

                    <div className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">
                            {p?.name}
                          </h3>
                          <p className="text-xs text-gray-500 truncate">
                            {p?.category}
                          </p>
                        </div>
                      </div>

                      <div className="mt-3 flex items-center justify-between">
                        <span className="font-bold text-gray-900">
                          ₦{Number(p?.finalPrice || 0).toLocaleString()}
                        </span>

                        {!!p?.price && (
                          <span className="line-through text-red text-sm font-semibold">
                            ₦{Number(p?.price || 0).toLocaleString()}
                          </span>
                        )}
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (soldOut) return;
                          addToCart(p);
                          toast.success("Added to cart");
                        }}
                        disabled={soldOut}
                        className={`mt-4 w-full py-2.5 rounded-xl flex items-center justify-center gap-2 font-semibold transition
                          ${
                            soldOut
                              ? "bg-gray-200 text-gray-600 cursor-not-allowed"
                              : "bg-red text-white hover:opacity-90"
                          }`}
                      >
                        <FaShoppingCart />
                        {soldOut ? "Sold Out" : "Add to Cart"}
                      </button>
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
        </Swiper>

        {/* Empty */}
        {!isPending && !isError && relatedProducts.length === 0 && (
          <div className="mt-4 bg-gray-50 rounded-2xl border border-gray-100 p-5 text-center">
            <p className="text-gray-900 font-semibold">No related products</p>
            <p className="text-sm text-gray-500 mt-1">
              Try browsing more items in this category.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default RelatedProduct;
